import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  Divider
} from '@mui/material';
import {
  WavingHand as WelcomeIcon,
  RouteOutlined as RouteIcon,
  TrendingUp as ElevationIcon
} from '@mui/icons-material';

interface WelcomeModalProps {
  open: boolean;
  onClose: () => void;
}

const WelcomeModal: React.FC<WelcomeModalProps> = ({ open, onClose }) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleClose = () => {
    if (dontShowAgain) {
      localStorage.setItem('cyclingbuddy_welcome_dismissed', 'true');
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          padding: 1
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <WelcomeIcon sx={{ color: 'var(--color-primary)', fontSize: 32 }} />
          <Typography variant="h5" component="span" fontWeight={600}>
            Welcome to CyclingBuddy!
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pb: 0 }}>
        <Typography variant="body1" paragraph sx={{ mt: 1 }}>
          Your companion for planning perfect cycling routes.
        </Typography>

        <Box sx={{ my: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RouteIcon sx={{ color: 'var(--color-accent)' }} />
            What can you do?
          </Typography>
          
          <Box component="ul" sx={{ pl: 2, mt: 1 }}>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              <strong>Plan Routes:</strong> Click on the map to create custom cycling routes with multiple waypoints
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              <strong>Analyze Elevation:</strong> View detailed elevation profiles with gradient-based color coding
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              <strong>Surface Analysis:</strong> See road surface types and conditions along your route
            </Typography>
            <Typography component="li" variant="body2">
              <strong>Save, Share & Download:</strong> Store your routes, share them with others, and export as GPX for GPS devices
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box
          sx={{
            bgcolor: 'rgba(255, 152, 0, 0.1)',
            border: '1px solid rgba(255, 152, 0, 0.3)',
            borderRadius: 1,
            p: 1.5,
            mb: 2
          }}
        >
          <Typography variant="body2" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ElevationIcon sx={{ fontSize: 18, color: '#ff9800' }} />
            Beta Status
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            CyclingBuddy is in <strong>beta</strong>. Found a bug or have an idea? Contact us at{' '}
            <a 
              href="mailto:mario@cyclingbuddy.de"
              style={{ 
                color: 'var(--color-primary)', 
                textDecoration: 'underline',
                fontWeight: 500
              }}
            >
              mario@cyclingbuddy.de
            </a>
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between', alignItems: 'center' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              size="small"
              sx={{
                color: 'var(--color-primary)',
                '&.Mui-checked': {
                  color: 'var(--color-primary)',
                },
                padding: '4px'
              }}
            />
          }
          label={<Typography variant="caption">Don't show again</Typography>}
          sx={{ m: 0 }}
        />
        <Button
          onClick={handleClose}
          variant="contained"
          sx={{
            bgcolor: 'var(--color-primary)',
            '&:hover': {
              bgcolor: 'var(--color-accent)',
            },
            textTransform: 'none',
            px: 4,
            whiteSpace: 'nowrap'
          }}
        >
          Get Started
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WelcomeModal;
